import { z } from 'zod';

export const CHAT_SUGGESTIONS_CACHE_VERSION = 'navigate-kind-v7';
export const SUGGESTION_TITLE_MIN_LENGTH = 4;
export const SUGGESTION_ACTION_MAX_LENGTH = 300;
export const SUGGESTION_TITLE_TARGET_LENGTH = 42;
export const SUGGESTION_ACTION_TARGET_LENGTH = 130;

export const suggestedActionSchema = z.object({
  title: z
    .string()
    .min(SUGGESTION_TITLE_MIN_LENGTH)
    .describe(
      `A very short sidebar chip label (ideally 4-8 words, max ${SUGGESTION_TITLE_TARGET_LENGTH} characters): a "Show me ..." phrase for the navigation suggestion, otherwise a review question focused on the core human-review risk.`,
    ),
  action: z
    .string()
    .describe(
      `One compact sentence sent verbatim to the review assistant when the chip is clicked (max ${SUGGESTION_ACTION_TARGET_LENGTH} characters).`,
    ),
  // Optional so a model omission degrades to title-prefix detection on the
  // client instead of failing generateObject and dumping users to fallbacks.
  kind: z
    .enum(['navigate', 'question'])
    .optional()
    .describe(
      `"navigate" only for the code-navigation suggestion (the "Show me ..." chip); "question" for review questions.`,
    ),
});

export const suggestionsSchema = z.object({
  suggestions: z
    .array(suggestedActionSchema)
    .describe(
      'An array of 2-3 suggestions: a code-navigation suggestion first when the PR context names code locations, then high-signal review questions.',
    ),
});

export type SuggestedAction = z.infer<typeof suggestedActionSchema>;

export const CHAT_FOLLOW_UPS_CACHE_VERSION = 'follow-ups-v1';

export const followUpSuggestionSchema = z.object({
  title: z
    .string()
    .min(SUGGESTION_TITLE_MIN_LENGTH)
    .describe(
      `A very short pill label (ideally 4-8 words, max ${SUGGESTION_TITLE_TARGET_LENGTH} characters) for the follow-up.`,
    ),
  action: z
    .string()
    .describe(
      `One compact sentence sent verbatim to the review assistant when the pill is clicked (max ${SUGGESTION_ACTION_TARGET_LENGTH} characters).`,
    ),
  // Optional for the same reason as suggestedActionSchema.kind: with
  // strictJsonSchema disabled, a model omission must degrade (server defaults
  // to "question") instead of failing generateObject and losing the pill.
  kind: z
    .enum(['navigate', 'question'])
    .optional()
    .describe(
      `"question" for a review question; "navigate" only when the action asks to show one specific changed file.`,
    ),
});

export const followUpsSchema = z.object({
  // No zod max(): an over-eager model returning extra items should be trimmed
  // by the route, not turned into a validation failure that loses everything.
  suggestions: z
    .array(followUpSuggestionSchema)
    .describe(
      'At most one high-signal follow-up grounded in the last exchange; empty when nothing is worth following up.',
    ),
});

export type LlmFollowUpSuggestion = z.infer<typeof followUpSuggestionSchema>;
